import { dateFormat } from '@constants/dateFormat'
import { useApi } from '@gravitate-js/the-armory'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import { getAPIURL } from '@utils/api'
import { type FormInstance, message } from 'antd'
import moment from 'moment/moment'
import { getCurrentMarket } from '@utils/general'
import {
  type InferResponse,
  useApiClient,
  cancelTypedQueries,
  getTypedQueriesData,
  setTypedQueriesData,
  getTypedQueryData,
  setTypedQueryData,
  invalidateTypedQueries,
  removeTypedQueries,
} from '@/hooks/useApiClient'
import type { components } from '@/types/api/v1'

export type OrderMovementResponse = InferResponse<'/bol/order_movements'>
export type OrderMovement = OrderMovementResponse['rows'][number]
export type RankedEbolResponse = { bol: Gravitate.ShortBol; ebols: Gravitate.Ebol[] }

type Ebol = components['schemas']['EBolResponse']
type SourceObject = components['schemas']['SourceObject']

type SaveManyResponse = {
  orders: OrderMovement[]
  errors?: string[]
  result: string
  success: { [key: number]: string }
}

export const useOrderMovements = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  // Queries
  const useOrderMovementsQuery = (filter, status, isCompact) =>
    useQuery({
      queryKey: ['orderMovementsQuery', filter, status, isCompact],
      queryFn: (): Promise<OrderMovementResponse> =>
        api.post(
          'bol/order_movements',
          { filter },
          { queryParams: new URLSearchParams({ status }), ignoreDefaults: isCompact }
        ),
      enabled: !!filter && !!status,
    })

  const useMovementErrorReasonCodes = () =>
    useQuery({
      queryKey: ['movementErrorReasonCodes'],
      queryFn: () => api.post('bol/order_movement_error_override_reason_codes'),
    })

  //todo: remove temp var mapping
  const useOrderMovementDetailQuery = (orderId?: string) =>
    useQuery({
      queryKey: ['orderMovementDetailQuery', orderId],
      queryFn: (): Promise<Gravitate.OrderMovementDetails> => normalizeDetail(api, orderId),
      enabled: !!orderId,
    })

  const useUnmatchedEbols = () => apiClient.useQuery('post', '/bol/ebol/all_unmatched')

  const useRankedEbols = (bolId?: string) =>
    apiClient.useQuery(
      'post',
      '/bol/ebol/ranked_options',
      {
        params: { query: { bol_id: bolId ?? '' } },
      },
      { enabled: !!bolId }
    )

  const useMatchedEbol = (bolId?: string) =>
    apiClient.useQuery(
      'post',
      '/bol/ebol/view_match',
      {
        params: { query: { bol_id: bolId ?? '' } },
      },
      { enabled: !!bolId }
    )

  const useAllocatedBolsQuery = (filter) =>
    useQuery({
      queryKey: ['allocatedBolsQuery', filter],
      queryFn: () => setIdsOnRows(api, 'bol/allocated_bols/overview', { filter, market: getCurrentMarket() }, false),
      enabled: !!filter,
    })

  // Mutations
  const useMatchBolMutation = (orderId: string) =>
    apiClient.useMutation('post', '/bol/ebol/match_bol', {
      onSuccess: (response) =>
        onEbolMatchChange({
          orderId: orderId,
          response,
          queryClient,
        }),
    })

  const useUnmatchBolMutation = (orderId: string) =>
    apiClient.useMutation('post', '/bol/ebol/unmatch_bol', {
      onSuccess: (response) =>
        onEbolMatchChange({
          orderId: orderId,
          response,
          queryClient,
        }),
    })

  const useRemoveEbol = () =>
    apiClient.useMutation('post', '/bol/ebol/delete', {
      onMutate: async ({ params }) => optimisticRemoveEbol(queryClient, params.query.ebol_id),
      onError: (_, _variables, ctx) => onRemoveFailed(queryClient, ctx as EbolDeleteContext | undefined), //todo: fix type in openapi-react-query
      onSettled: () => {
        invalidateTypedQueries(queryClient, 'post', '/bol/ebol/ranked_options')
        invalidateTypedQueries(queryClient, 'post', '/bol/ebol/all_unmatched')
        invalidateTypedQueries(queryClient, 'post', '/bol/ebol/view_match')
      },
    })

  const useAddEbolMapping = () =>
    apiClient.useMutation('post', '/bol/ebol/add_mapping', {
      onMutate: ({ body }) => {
        const tempId = crypto.randomUUID()
        updateEbolQueries(queryClient, body, (source) =>
          addIdNameToSourceObject(source, { id: tempId, name: body.name })
        )
        return tempId
      },
      onError: (_, { body }, context) => {
        const tempId = context as string | undefined
        if (!tempId) return
        updateEbolQueries(queryClient, body, (source) => removeIdNameFromSourceObject(source, tempId))
      },
      onSuccess: ({ mapped_object, auto_match_occurred }, { body }, context) => {
        const tempId = context as string | undefined
        if (!tempId) return

        if (auto_match_occurred) {
          queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery'] })
          queryClient.invalidateQueries({ queryKey: ['orderMovementsQuery'] })
          invalidateTypedQueries(queryClient, 'post', '/bol/ebol/all_unmatched')
          removeTypedQueries(queryClient, 'post', '/bol/ebol/view_match')
          removeTypedQueries(queryClient, 'post', '/bol/ebol/ranked_options')
          return
        }
        updateEbolQueries(queryClient, body, (source) => setIdNameIds(source, tempId, mapped_object.id))
      },
    })

  const saveBOLMutation = () =>
    useMutation({
      mutationKey: ['saveBOLMutation'],
      mutationFn: (request) => api.post('bol/save_many', request.bols),
      onSuccess: (movementResponse: SaveManyResponse, request) => {
        const ids = Object.values(movementResponse?.success ?? {})
        const newMovement = movementResponse?.orders[0]
        if (request.shouldRefresh) {
          refreshMovements(newMovement, queryClient)
        } else {
          queryClient.setQueriesData({ queryKey: ['orderMovementDetailQuery', newMovement?.order_id] }, (oldData) => {
            const result = {
              ...oldData,
              bols: request?.bols?.map((bol, bolIndex) => ({
                ...bol,
                bol_id: ids[bolIndex],
                key: ids[bolIndex],
                date: moment(bol.date), // normalize dates so antd inputs don't cry
                details: bol.details.map((d, detailIndex) => {
                  // spread selected pocket onto new bols
                  const compartment_index = request.bols[bolIndex].details[detailIndex].compartment_index
                  const isLoadVolume = compartment_index === null || typeof compartment_index === 'undefined'
                  return {
                    ...d,
                    pocket: isLoadVolume ? null : compartment_index + 1,
                  }
                }),
              })),
            }
            return result
          })
        }
      },
    })

  const useSaveCompactOrderMovementsMutation = (form: FormInstance, closeDrawer: () => void) =>
    useMutation({
      mutationKey: ['saveCompactOrderMovements'],
      mutationFn: (form: any) => api.post('store_monitor/site_view/create_order_movements', form),
      onSuccess: (movementResponse, request) => {
        message.success('Order movements created successfully.')
        closeDrawer()
        form.resetFields()
        queryClient.invalidateQueries({ queryKey: ['siteViewOrders'] })
      },
    })

  const saveDropMutation = () =>
    useMutation({
      mutationKey: ['saveDropMutation'],
      mutationFn: (request) => api.post('bol/drop_save_many', request.drops),
      onSuccess: (movementResponse, request) => {
        const newMovement = movementResponse?.orders[0]
        if (request.shouldRefresh) {
          refreshMovements(newMovement, queryClient)
        } else {
          queryClient.setQueriesData({ queryKey: ['orderMovementDetailQuery', newMovement?.order_id] }, (oldData) => ({
            ...oldData,
            store_drops: request?.drops?.map((drop) => ({
              ...drop,
              date: moment(drop.date),
              time: moment(drop.time),
              key: drop.uuid,
            })),
          }))
        }
      },
    })

  const approveMovementErrorMutation = () =>
    useMutation({
      mutationKey: ['approveMovementError'],
      mutationFn: (request) => api.post('bol/bypass_movement_validation', request),
      onSuccess: (newMovement, request) => {
        refreshMovements(newMovement, queryClient)
      },
    })

  const useBypassValidationIssueMutation = (mutationProps: { afterSuccess?: Function }) =>
    useMutation({
      mutationKey: ['bypassValidationIssue'],
      mutationFn: (order_id: number) =>
        api.post('store_monitor/site_view/bypass_validation_issue', null, {
          queryParams: new URLSearchParams({ order_id: String(order_id) }),
        }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['siteViewOrders'] })
        mutationProps?.afterSuccess()
      },
    })

  const useDeleteDropAttachment = () =>
    apiClient.useMutation('post', '/bol/delete_drop_attachments', {
      onSuccess: (_, vars) => {
        // vars is expected to contain params: { order_number, store_number, photo }
        const order_number = vars?.params?.order_number
        if (order_number !== undefined) {
          queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', order_number] })
        }
      },
    })

  const useUploadDropAttachment = () =>
    apiClient.useMutation('post', '/in_cab/upload_drop_attachments', {
      onSuccess: (_, vars) => {
        // vars is expected to contain params: { order_id, site_number } or similar
        const order_id = vars?.params?.order_id
        if (order_id !== undefined) {
          queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', order_id] })
        }
      },
    })

  return {
    useOrderMovementsQuery,
    useOrderMovementDetailQuery,
    useAllocatedBolsQuery,
    useUnmatchedEbols,
    useMatchedEbol,
    useRankedEbols,
    saveBOLMutation,
    saveDropMutation,
    useMovementErrorReasonCodes,
    approveMovementErrorMutation,
    useSaveCompactOrderMovementsMutation,
    useBypassValidationIssueMutation,
    useUnmatchBolMutation,
    useMatchBolMutation,
    useRemoveEbol,
    useAddEbolMapping,
    useDeleteDropAttachment,
    useUploadDropAttachment,
  }
}

const normalizeDetail = async (api, orderId) => {
  const data = await api.post('bol/order_movement_details', undefined, {
    queryParams: new URLSearchParams({ order_id: orderId }),
  })
  setDatesToDisplay(data, 'bols')
  setDatesToDisplay(data, 'store_drops')
  return data
}

const setDatesToDisplay = (detail, name) => {
  detail[name]?.forEach((d) => {
    if (!d.date) return
    // Keep the original date as a moment object without altering the timezone
    // The API sends UTC timestamps, let the display layer handle timezone conversion
    d.date = moment(d.date)
    d.time = d.time ? moment(d.time) : moment(d.date)
  })
}

const refreshMovements = (newMovement, queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', newMovement?.order_id] })
  queryClient.setQueriesData({ queryKey: ['orderMovementsQuery'] }, (oldData) => {
    if (!oldData) return

    return {
      ...oldData,
      rows: oldData.rows.map((movement) => (newMovement?.order_id === movement.order_id ? newMovement : movement)),
    }
  })
}

const setIdsOnRows = async (api, url, searchParams, returnsRows) => {
  if (returnsRows) {
    const rows = await api.post(url, undefined, {
      queryParams: new URLSearchParams(searchParams),
    })
    return rows?.map((row) => {
      row.id = crypto.randomUUID()
      return row
    })
  } else {
    const data = await api.post(url, searchParams)

    data.rows = data?.rows?.map((row) => {
      row.id = crypto.randomUUID()
      return row
    })
    return data
  }
}

type EbolMatchChangeArgs = {
  orderId: string
  response: OrderMovement
  queryClient: QueryClient
}

const onEbolMatchChange = ({ orderId, response, queryClient }: EbolMatchChangeArgs) => {
  queryClient.invalidateQueries({ queryKey: ['orderMovementDetailQuery', orderId] })
  queryClient.setQueriesData({ queryKey: ['orderMovementsQuery'] }, (data?: OrderMovementResponse) =>
    setCachedOrderMovement(orderId, (x) => ({ ...x, ...response }), data)
  )
  invalidateTypedQueries(queryClient, 'post', '/bol/ebol/all_unmatched')
  removeTypedQueries(queryClient, 'post', '/bol/ebol/ranked_options')
  removeTypedQueries(queryClient, 'post', '/bol/ebol/view_match')
}

const setCachedOrderMovement = (
  id: string,
  setter: (previous: OrderMovement) => OrderMovement,
  data?: OrderMovementResponse
) => {
  if (!data?.rows?.length) return data

  return {
    ...data,
    rows: data.rows.map((x) => (x.id !== id ? x : setter(x))),
  }
}
type IdName = NonNullable<SourceObject['id_names']>[0]
const addIdNameToSourceObject = ({ source_id, id_names }: SourceObject, idName: IdName): SourceObject => ({
  source_id,
  id_names: [...(id_names ?? []), idName],
})
const removeIdNameFromSourceObject = ({ source_id, id_names }: SourceObject, id: string): SourceObject => ({
  source_id,
  id_names: id_names?.filter((x) => x.id !== id),
})
const setIdNameIds = ({ source_id, id_names }: SourceObject, oldId: string, newId: string): SourceObject => ({
  source_id,
  id_names: id_names?.map((x) => ({ id: x.id === oldId ? newId : x.id, name: x.name })),
})

const updateEbolQueries = (
  queryClient: QueryClient,
  { field, source_system, source_id }: components['schemas']['AddMappingRequest'],
  operation: (obj: SourceObject) => SourceObject
) => {
  const updater = (ebol: Ebol) => {
    if (ebol.source_system !== source_system) return ebol
    if (field === 'supplier' && ebol.supplier.source_id === source_id) {
      return {
        ...ebol,
        supplier: operation(ebol.supplier),
      }
    }
    if (field === 'terminal' && ebol.terminal.source_id === source_id) {
      return {
        ...ebol,
        terminal: operation(ebol.terminal),
      }
    }
    if (field === 'product') {
      return {
        ...ebol,
        details: ebol.details.map((detail) => {
          if (detail.product.source_id !== source_id) return detail
          return {
            ...detail,
            product: operation(detail.product),
          }
        }),
      }
    }
    return ebol
  }
  setTypedQueriesData(queryClient, 'post', '/bol/ebol/ranked_options', {}, (oldData) => {
    if (!oldData) return
    return {
      ...oldData,
      ebols: oldData.ebols.map(updater),
    }
  })
  setTypedQueriesData(queryClient, 'post', '/bol/ebol/all_unmatched', {}, (oldData) => {
    if (!oldData) return
    return oldData.map(updater)
  })
  setTypedQueriesData(queryClient, 'post', '/bol/ebol/view_match', {}, (oldData) => {
    if (!oldData) return
    return {
      ...oldData,
      ebol: updater(oldData.ebol),
    }
  })
}

const optimisticRemoveEbol = async (queryClient: QueryClient, ebolId: string) => {
  await Promise.all([
    cancelTypedQueries(queryClient, 'post', '/bol/ebol/ranked_options'),
    cancelTypedQueries(queryClient, 'post', '/bol/ebol/all_unmatched'),
    cancelTypedQueries(queryClient, 'post', '/bol/ebol/view_match'),
  ])
  const previousRanked = getTypedQueriesData(queryClient, 'post', '/bol/ebol/ranked_options')
  setTypedQueriesData(queryClient, 'post', '/bol/ebol/ranked_options', {}, (data) =>
    data ? { ...data, ebols: data.ebols.filter((x) => x._id !== ebolId) } : undefined
  )

  const previousUnmatched = getTypedQueryData(queryClient, 'post', '/bol/ebol/all_unmatched')
  setTypedQueryData(queryClient, 'post', '/bol/ebol/all_unmatched', {}, (data) => data?.filter((x) => x._id !== ebolId))
  return { previousRanked, previousUnmatched }
}

type UnwrapPromise<T> = T extends Promise<infer R> ? R : never
type EbolDeleteContext = UnwrapPromise<ReturnType<typeof optimisticRemoveEbol>>
const onRemoveFailed = (queryClient: QueryClient, ctx?: EbolDeleteContext) => {
  ctx?.previousRanked.forEach(([key, value]) => {
    queryClient.setQueryData(key, value)
  })
  if (ctx?.previousUnmatched)
    setTypedQueryData(queryClient, 'post', '/bol/ebol/all_unmatched', {}, ctx.previousUnmatched)
}
